import { MAIN_API } from 'config/constants/endpoints'
import { useEffect, useMemo, useState } from 'react'
import { ChainId } from '@echodex/sdk'
import { useActiveChainId } from './useActiveChainId'

export enum MainApiRoute {
    GET_AIRDROP_USER_INFO = '/getAirdropUserInfo',
    GET_LEADER_BOARD_USER = '/getAirdropLeaderBoard',
    CHECK_IN = '/checkin',
    GET_TOTAL_TRANSACTIONS = '/getTotalTransactions'
}

export type MainApiParams = {
    [MainApiRoute.GET_AIRDROP_USER_INFO]: {
        address: string
    }
    [MainApiRoute.GET_LEADER_BOARD_USER]: undefined,
    [MainApiRoute.CHECK_IN]: {
        address: string
    }
    [MainApiRoute.GET_TOTAL_TRANSACTIONS]: undefined
}

export type MainApiResult = {
    [MainApiRoute.GET_AIRDROP_USER_INFO]: {
        point: number
        histories: [
            {
                id: number
                address: string
                type: string
                amount_usd: string
                point: string
                created_at: string
            },
        ]
    }
    [MainApiRoute.GET_LEADER_BOARD_USER]: [{ address: string; point: string }]
    [MainApiRoute.CHECK_IN]: {
        success: boolean
    }
    [MainApiRoute.GET_TOTAL_TRANSACTIONS]: string
}

const requiredParams: { [key in MainApiRoute]: (keyof MainApiParams[key])[] } = {
    [MainApiRoute.GET_AIRDROP_USER_INFO]: ['address'],
    [MainApiRoute.GET_LEADER_BOARD_USER]: [],
    [MainApiRoute.CHECK_IN]: ['address'],
    [MainApiRoute.GET_TOTAL_TRANSACTIONS]: [],
}

export function convertParamsToString(params: MainApiParams[MainApiRoute.GET_AIRDROP_USER_INFO]) {
    return Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
}

export async function callMainApi<T extends keyof MainApiParams>(chainId: ChainId, route: T, searchParams?: string): Promise<MainApiResult[T]> {
    return new Promise((resolve, reject) => {
        const url = new URL(route, MAIN_API[chainId])
        if (searchParams) url.search = searchParams
        fetch(url.toString())
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`)
                }
                return response.json() as Promise<{ data: MainApiResult[T] }>
            })
            .then((data) => resolve(data.data))
            .catch((error) => reject(error))
    })
}

export function useMainApi<T extends keyof MainApiParams>(route: T, params?: MainApiParams[T]) {
    const { chainId } = useActiveChainId()
    const [result, setResult] = useState<MainApiResult[T] | null>(null)

    const searchParams = useMemo(() => {
        if (!params) return ''
        return convertParamsToString(params)
    }, [params])

    useEffect(() => {
        if (!chainId) return
        if (requiredParams[route].some((key) => !params[key])) return
        callMainApi(chainId, route, searchParams).then(res => setResult(res))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route, chainId, searchParams])

    return result as MainApiResult[T] | null
}
